import {getHighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {fetchQuery, useRelayEnvironment} from 'react-relay'
import {
  createOperationDescriptor,
  type Disposable,
  getRequest,
  type GraphQLTaggedNode,
  type Variables,
} from 'relay-runtime'

import {rollupEvents} from '../../utils/timeline-rollups'
import type {useTimelineHighlightItems$data} from './documents/__generated__/useTimelineHighlightItems.graphql'
import type {TimelineItem} from './IssueTimeline'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {isEventHighlighted, useTimelineHighlights} from './use-timeline-highlight'

// ─── Types ───────────────────────────────────────────────────────────────────

type TimelineCounters = {
  frontItems: number
  highlightItems: number
  highlightStartPosition: number
  backItems: number
}

/**
 * The normalised edge shape shared by all timeline connections (front, back,
 * conversation, activity). Every caller must reduce its Relay fragment data
 * to this shape before passing it to the core hook.
 */
export type TimelineEdgeData = {
  totalCount: number
  edges:
    | ReadonlyArray<
        | {
            readonly node:
              | {
                  readonly __id: string
                }
              | null
              | undefined
          }
        | null
        | undefined
      >
    | null
    | undefined
}

/**
 * Configuration for the back-items fetch query.
 *
 * All back-item queries follow the same pattern:
 * `{ node(id) { ... on Issue { <connectionKey>: timelineItems(...) { ... } } } }`
 *
 * Use `createBackFetcherConfig` to build one from a query + connection key.
 */
export type BackFetcherConfig = {
  query: GraphQLTaggedNode
  makeVariables: (issueId: string, count: number, cursor: string | null) => Variables
  extractItems: (response: unknown) => {
    items: IssueTimelineItemFragment[]
    startCursor: string | null
    hasPreviousPage: boolean
  } | null
}

type BackConnectionData = TimelineEdgeData & {pageInfo: {startCursor: string | null; hasPreviousPage?: boolean}}

/**
 * Factory for creating a `BackFetcherConfig` from a query and its connection
 * property name on the Issue node (e.g. `'backTimelineItems'`).
 *
 * All back-item queries share the same variables shape and response structure,
 * so this eliminates the boilerplate that would otherwise be duplicated across
 * classic, conversation, and activity hooks.
 */
export function createBackFetcherConfig(query: GraphQLTaggedNode, connectionKey: string): BackFetcherConfig {
  return {
    query,
    makeVariables: (issueId, count, cursor) => ({id: issueId, count, cursor}),
    extractItems: resp => {
      const response = resp as {node?: Record<string, unknown> | null}
      const issueNode = response.node
      if (issueNode && connectionKey in issueNode) {
        const backData = issueNode[connectionKey] as BackConnectionData | null | undefined
        if (backData) {
          return {
            items: mapAndFilterTimelineItems(backData),
            startCursor: backData.pageInfo.startCursor ?? null,
            hasPreviousPage: backData.pageInfo.hasPreviousPage ?? true,
          }
        }
      }
      return null
    },
  }
}

export type UseTimelineItemsCoreProps = {
  issueId: string
  /** Raw edge data from the front pagination fragment. Mapped to items inside the core. */
  frontData: TimelineEdgeData | undefined
  /** Raw edge data from the back pagination fragment. Mapped to items inside the core. */
  backData: TimelineEdgeData | undefined
  /** Forward pagination function from `usePaginationFragment`. */
  loadMoreFrontItems: (count: number) => void
  /** Fetcher configuration for loading back items. */
  backFetcherConfig: BackFetcherConfig
  /** Optional: highlighted event text (e.g. "issuecomment-456"). */
  highlightedEvent?: string | undefined
  /** Optional: page number for bot pagination. */
  page?: number
  /** Optional: Maximum number of back items to auto-load on initial render. Defaults to 15. */
  initialBackPageSize?: number
}

export type UseTimelineItemsCoreResult = {
  timelineItems: TimelineItem[] | null
  totalItemCount: number
  highlightedReady: boolean
}

// ─── Core Hook ───────────────────────────────────────────────────────────────

/**
 * Generic hook responsible for combining front, highlight, and back items
 * into a single paginated timeline list. Handles:
 *
 * - Auto-loading initial back items
 * - Highlighted event fetching & stitching
 * - Load-more markers between sections
 * - Deduplication & rollups
 *
 * Consumers provide raw edge data from their Relay fragments and a fetcher
 * config for the back-items query. This allows the same core logic to be used
 * by the timeline with different fragments.
 */
export function useTimelineItemsCore({
  issueId,
  frontData,
  backData,
  loadMoreFrontItems,
  backFetcherConfig,
  highlightedEvent,
  page,
  initialBackPageSize = 15,
}: UseTimelineItemsCoreProps): UseTimelineItemsCoreResult {
  // This object is responsible for keeping track of the positioning and number of items in each section of the timeline.
  // That state depends on responses from the server but also user actions, so we aggregate the data here.
  const [timelineCounters, setTimelineCounters] = useState<TimelineCounters>({
    frontItems: 0,
    highlightItems: 0,
    highlightStartPosition: 0,
    backItems: 0,
  })

  // This state is used to determine if the highlighted event has been fetched to prevent reloading if
  // a user clicks a comment link more than once in succession.
  // We reset this state when the highlighted event changes.
  const [fetched, setFetched] = useState(false)
  // This state is used to determine if the user has clicked on a different highlighted event
  const [lastFetchedHighlight, setLastFetchedHighlight] = useState<string | null>(null)
  const [lastTotalBeforeFocus, setLastTotalBeforeFocus] = useState<number | null>(null)
  const [shouldLoadBackItems, setShouldLoadBackItems] = useState(false)
  const [loadingInitialBackItems, setLoadingInitialBackItems] = useState(false)

  // State for managing custom back items pagination
  const [backItems, setBackItems] = useState<IssueTimelineItemFragment[]>([])
  const [backItemsCursor, setBackItemsCursor] = useState<string | null>(null)
  const [isLoadingBackItems, setIsLoadingBackItems] = useState(false)

  const environment = useRelayEnvironment()

  // Retain the operation descriptor for each back-items page so Relay's GC can't collect the
  // records it brought in while `backItems` still holds snapshots of them. The comment mutations
  // (delete, minimize, update body) commit against those records and connections by id, so a
  // collected record would leave them writing into an empty store. Disposed on unmount.
  const backPageDisposablesRef = useRef<Disposable[]>([])
  useEffect(() => {
    return () => {
      for (const disposable of backPageDisposablesRef.current) {
        disposable.dispose()
      }
      backPageDisposablesRef.current = []
    }
  }, [])

  // Custom loadMoreBackItems function using fetchQuery
  const loadMoreBackItems = useCallback(
    (count: number, options?: {onComplete?: () => void}) => {
      if (isLoadingBackItems) return

      setIsLoadingBackItems(true)

      const variables = backFetcherConfig.makeVariables(issueId, count, backItemsCursor)
      const operation = createOperationDescriptor(getRequest(backFetcherConfig.query), variables)
      const disposable = environment.retain(operation)
      backPageDisposablesRef.current.push(disposable)

      fetchQuery(environment, backFetcherConfig.query, variables, {fetchPolicy: 'store-or-network'}).subscribe({
        next: (response: unknown) => {
          const extracted = backFetcherConfig.extractItems(response)
          if (extracted) {
            setBackItems(prev => [...extracted.items, ...prev])
            setBackItemsCursor(extracted.startCursor)
          }
        },
        complete: () => {
          setIsLoadingBackItems(false)
          options?.onComplete?.()
        },
        error: () => {
          // The fetch failed, so the operation has no useful records to keep
          // alive. Drop the retain to avoid leaking it across the component's
          // lifetime.
          disposable.dispose()
          backPageDisposablesRef.current = backPageDisposablesRef.current.filter(d => d !== disposable)
          setIsLoadingBackItems(false)
          options?.onComplete?.()
        },
      })
    },
    [backItemsCursor, environment, isLoadingBackItems, issueId, backFetcherConfig],
  )

  const totalItemCount = Math.max(frontData?.totalCount ?? 0, 0)

  const frontItems = useMemo(() => mapAndFilterTimelineItems(frontData), [frontData])
  const emptyBackItems = useMemo(() => mapAndFilterTimelineItems(backData), [backData])

  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])
  // This means that the highlighted item is already in the front or back items so no need to go back to the server
  const highlightIsPreloaded = useMemo(
    () =>
      highlight
        ? [...frontItems, ...backItems, ...emptyBackItems].some(event => isEventHighlighted(event, highlight))
        : false,
    [backItems, frontItems, highlight, emptyBackItems],
  )
  const hasFrontTimelineData = !!frontData

  useEffect(() => {
    if (!hasFrontTimelineData) {
      throw new Error(
        `Missing front timeline items for issue ${issueId}.  After refetching, the frontTimelineItems should be defined.`,
      )
    }
  }, [hasFrontTimelineData, issueId])

  useEffect(() => {
    if (backItems.length + emptyBackItems.length === 0 && !page) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldLoadBackItems(true)
    }
  }, [backItems.length, emptyBackItems.length, page])

  useEffect(() => {
    if (shouldLoadBackItems) {
      const backItemsToFetch = Math.min(initialBackPageSize, totalItemCount - frontItems.length)
      if (backItemsToFetch > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLoadingInitialBackItems(true)
        loadMoreBackItems(backItemsToFetch, {
          onComplete: () => {
            setShouldLoadBackItems(false)
            setLoadingInitialBackItems(false)
          },
        })
      }
    }
  }, [loadMoreBackItems, frontItems.length, shouldLoadBackItems, totalItemCount, initialBackPageSize])

  const {
    data: highlightData,
    loadPrevious: loadBeforeHighlight,
    totalBeforeFocus,
    loadNext: loadAfterHighlight,
  } = useTimelineHighlights(issueId, highlight, !fetched && highlight && !highlightIsPreloaded)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLastTotalBeforeFocus(totalBeforeFocus)
  }, [totalBeforeFocus])

  const loadedHighlightItems = useMemo(() => {
    if (highlightData) {
      return mapAndFilterTimelineItems(highlightData)
    }
    return []
  }, [highlightData])

  useEffect(() => {
    if (highlightData) {
      if (highlightedEvent) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFetched(!(lastFetchedHighlight && highlightedEvent !== lastFetchedHighlight))
        setLastFetchedHighlight(highlightedEvent)
      }
    }
  }, [highlight, highlightData, highlightedEvent, lastFetchedHighlight])

  const hasLazyLoadedHighlights = loadedHighlightItems.length > 0

  const itemsRemainingFront = Math.max(
    hasLazyLoadedHighlights
      ? timelineCounters.highlightStartPosition - timelineCounters.frontItems
      : totalItemCount - frontItems.length - backItems.length - emptyBackItems.length,
    0,
  )

  const itemsRemainingBack = Math.max(
    hasLazyLoadedHighlights
      ? totalItemCount -
          (timelineCounters.highlightStartPosition + timelineCounters.highlightItems) -
          timelineCounters.backItems
      : totalItemCount - frontItems.length - backItems.length - emptyBackItems.length,
    0,
  )

  // If a highlighted event (or its neighbor) exists in the front or back items, we need to remove it from the loaded highlight items
  const uniqueLoadedHighlightItems = useMemo(() => {
    if (hasLazyLoadedHighlights) {
      return subtractItems(loadedHighlightItems, [...frontItems, ...backItems, ...emptyBackItems])
    }
    return []
  }, [frontItems, backItems, emptyBackItems, loadedHighlightItems, hasLazyLoadedHighlights])

  const timelineItems: TimelineItem[] | null = useMemo(() => {
    // Return null (error state) when we expect items but none have loaded in
    // any connection. We must also check back items because mutations (e.g.
    // adding a comment) append edges to the back connection and increment
    // totalCount on the front connection. Without this check an initially-empty
    // timeline would return null as soon as the first comment is added.
    if (frontItems.length === 0 && emptyBackItems.length === 0 && backItems.length === 0 && totalItemCount !== 0) {
      return null
    }

    // If we loaded duplicated records from the front and back load, we dedupe them to prevent accidental duplicates
    //
    // This can happen if for some reason we get more items than there is in the issue
    // like when an event is deleted or a mismatch from live updates
    if (
      frontItems.length + uniqueLoadedHighlightItems.length + backItems.length + emptyBackItems.length >=
      totalItemCount
    ) {
      return rollupEvents(
        dedupeItems([...frontItems, ...uniqueLoadedHighlightItems, ...backItems, ...emptyBackItems]),
      ).map(item => ({
        type: 'event',
        ...item,
      }))
    }
    // Otherwise we render both arrays and inject the load buttons in the middle

    const frontTimelineItems: TimelineItem[] = rollupEvents(frontItems).map(item => ({
      type: 'event',
      ...item,
    }))

    const loadMoreFront: TimelineItem[] =
      itemsRemainingFront === 0 || page
        ? []
        : [
            {
              type: 'load',
              position: 'top',
              loadFromTop: loadMoreFrontItems,
              loadFromBottom: (count: number, options?: {onComplete?: () => void}) => {
                if (hasLazyLoadedHighlights) {
                  loadBeforeHighlight(count, options)
                } else {
                  loadMoreBackItems(count, options)
                }
              },
              numberOfRemainingItems: itemsRemainingFront,
              isCurrentlyLoading: loadingInitialBackItems && uniqueLoadedHighlightItems.length === 0,
            },
          ]

    const highlightTimelineItems: TimelineItem[] = !hasLazyLoadedHighlights
      ? []
      : rollupEvents(uniqueLoadedHighlightItems).map(item => ({
          type: 'event',
          ...item,
        }))

    const loadMoreBack: TimelineItem[] =
      !hasLazyLoadedHighlights || itemsRemainingBack === 0 || page
        ? []
        : [
            {
              type: 'load',
              position: 'bottom',
              loadFromTop: loadAfterHighlight,
              loadFromBottom: loadMoreBackItems,
              numberOfRemainingItems: itemsRemainingBack,
              isCurrentlyLoading: loadingInitialBackItems && uniqueLoadedHighlightItems.length > 0,
            },
          ]

    const backTimelineItems: TimelineItem[] = rollupEvents(dedupeItems([...backItems, ...emptyBackItems])).map(
      item => ({
        type: 'event',
        ...item,
      }),
    )

    return [...frontTimelineItems, ...loadMoreFront, ...highlightTimelineItems, ...loadMoreBack, ...backTimelineItems]
  }, [
    frontItems,
    totalItemCount,
    uniqueLoadedHighlightItems,
    backItems,
    emptyBackItems,
    itemsRemainingFront,
    page,
    loadMoreFrontItems,
    loadingInitialBackItems,
    hasLazyLoadedHighlights,
    itemsRemainingBack,
    loadAfterHighlight,
    loadMoreBackItems,
    loadBeforeHighlight,
  ])

  useEffect(() => {
    if (
      timelineCounters.frontItems !== frontItems.length ||
      timelineCounters.backItems !== backItems.length + emptyBackItems.length ||
      timelineCounters.highlightItems !== uniqueLoadedHighlightItems.length ||
      lastTotalBeforeFocus !== totalBeforeFocus
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimelineCounters(current => {
        return {
          frontItems: frontItems.length,
          backItems: backItems.length + emptyBackItems.length,
          highlightItems: uniqueLoadedHighlightItems.length,
          // This is required since in pagination requests the server will always return 'totalBeforeFocus: 0' even if we do have a highlighted item
          highlightStartPosition: totalBeforeFocus > 0 ? totalBeforeFocus : current.highlightStartPosition,
        }
      })
    }
  }, [
    frontItems,
    backItems,
    emptyBackItems,
    uniqueLoadedHighlightItems,
    totalBeforeFocus,
    timelineCounters.frontItems,
    timelineCounters.backItems,
    timelineCounters.highlightItems,
    lastTotalBeforeFocus,
  ])

  const highlightedReady = isHighlightReady(
    totalItemCount,
    backItems.length + emptyBackItems.length,
    timelineCounters,
    highlightIsPreloaded,
  )

  return {timelineItems, totalItemCount, highlightedReady}
}

// ─── Shared Utilities ────────────────────────────────────────────────────────

function isHighlightReady(
  totalItemCount: number,
  backItems: number,
  timelineCounters: TimelineCounters,
  highlightIsPreloaded: boolean,
) {
  // No highlighted item
  if (timelineCounters.highlightItems === 0 && !highlightIsPreloaded) return false

  const highlightedItemsInBack =
    timelineCounters.highlightStartPosition + timelineCounters.highlightItems > totalItemCount - 15
  const backItemsPending = totalItemCount > 15 && backItems === 0

  // Delay scrolling if the highlighted items overlap with the back items, and they aren't ready yet
  if (highlightedItemsInBack && backItemsPending) return false

  return true
}

/**
 * Takes a list of timeline items and dedupes them based on their __id
 */
const dedupeItems = (items: IssueTimelineItemFragment[]) =>
  items.reduce(
    ({keys, values}, item) => {
      if (!keys.has(item.__id)) {
        keys.add(item.__id)
        values.push(item)
      }

      return {keys, values}
    },
    {keys: new Set<string>(), values: [] as IssueTimelineItemFragment[]},
  ).values

/**
 * Takes two lists of timeline items and subtracts the second list from the first based on their __id
 */
const subtractItems = (items: IssueTimelineItemFragment[], subtract: IssueTimelineItemFragment[]) =>
  items.filter(item => !subtract.some(subtractItem => subtractItem.__id === item.__id))

/**
 * Maps raw edge data from any timeline connection to typed timeline items.
 * Works with front, back, conversation, activity, and highlight connections.
 */
function mapAndFilterTimelineItems(
  data: TimelineEdgeData | useTimelineHighlightItems$data['timelineItems'] | undefined,
): IssueTimelineItemFragment[] {
  if (!data) return []

  return (data.edges || []).reduce((items: IssueTimelineItemFragment[], edge) => {
    // RELAY-CLEANUP(timeline pagination): the spreading documents are still Relay-owned, so their
    // node types carry open-enum members and omit the `__is*` discriminators. Selections match.
    if (edge?.node?.__id) items.push(edge.node as IssueTimelineItemFragment)
    return items
  }, [])
}
